import { type SalesCampaignPrice, type SalesCampaignPriceDTO } from "~/domains/salesCampaign/types/SalesCampaign";
import { type PriceDataDTO } from "~/domains/pricing/types/PriceDataDTO";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import type { MoxyMapDTO } from "~/common/types/MoxyMapDTO";
import type { PnaPackagingDataRefinedDTO } from "~/common/types/PnaAvailabilityDTO";
import { zero } from "~/utils/PriceUtils";

interface BasePackagingDTO {
  packsize: number;
  code: string;
  primaryWarehouseCode: string;
  primaryWarehouseName: string;
}

export interface PriceInfoDTO {
  code: string;
  packCode?: string | null;
  sapMaterialNumber: string;
  hasPrice: boolean;
  maxQuantityAvailable?: number | null;
  netPrice?: PriceDataDTO | null;
  defaultPackaging?: BasePackagingDTO | null;

  priceScale?: MoxyMapDTO<number, PriceDataDTO | null> | null;
  priceScaleMSP?: MoxyMapDTO<number, PriceDataDTO | null> | null;
  priceScaleRSP?: MoxyMapDTO<number, PriceDataDTO | null> | null;
  isContractAvailable?: boolean | null;

  isFreshPnaCall?: boolean | null;

  listPrice?: PriceDataDTO | null;
  packagingInformationRefined?: PnaPackagingDataRefinedDTO[] | null;
  isInStock?: boolean | null;
  replenishmentLeadTimeDate?: string | null;
  replenishmentLeadTimeDays?: number | null;
  priceRSP?: PriceDataDTO | null;
  priceMSP?: PriceDataDTO | null;
  priceRSPUnit?: string | null;
  rebateScale?: MoxyMapDTO<number, string | null> | null;
  surchargeScale?: MoxyMapDTO<number, number | null> | null;
  isTariffSurchargeApplicable?: boolean | null;
  isSpecialPrice?: boolean | null;

  priceUnit?: number | null; // all prices above are "per priceUnit pieces". In case of null, assume 1
  minQtyAvailableScale?: number | null;
  priceReference?: string | null;
  originCountry?: string | null;
  salesCampaign?: SalesCampaignPriceDTO;
}

/** Raw MOXy response for getPrice and getLcPrice. */
export interface PriceAndPackageListDTO {
  items?: PriceInfoDTO[];
}

/** Raw MOXy response for getPriceList. */
export interface PriceAndPackageNestedListDTO {
  itemGroups?: PriceAndPackageListDTO[];
}

export type ScalingValue<T> = ({ minQty: number } & T)[];
export type ScalingPrice = ScalingValue<{ price: PriceData }>;

export interface PriceInfo {
  showBetterDealMinQty: number | null;
  code: string;
  packCode?: string;
  priceReference: string;
  sapMaterialNumber: string;
  hasPrice: boolean;
  priceScale: ScalingPrice;
  priceScaleMSP: { minQty: number; price?: PriceData }[];
  priceScaleRSP: { minQty: number; price?: PriceData }[];
  isContractAvailable: boolean;
  isFreshPnaCall: boolean;

  listPrice: PriceData;
  rebateScale: { minQty: number; rebate?: string }[];
  surchargeScale: { minQty: number; surcharge: number }[];
  isTariffSurchargeApplicable: boolean;
  isSpecialPrice: boolean;

  priceUnit: number;

  salesCampaign?: SalesCampaignPrice;
}

export interface PriceInfoEntry {
  minQty: number;
  netPrice: PriceData;
  msp?: PriceData;
  rsp?: PriceData;
  rebate?: string;
  surcharge: number;
  tariffSurcharge: number;
}

function transformPriceDTO(dto: PriceDataDTO | PriceData | null | undefined): PriceData {
  if (!dto) {
    return zero();
  }
  return {
    currencyIso: dto.currencyIso,
    value: dto.value > 0 ? dto.value : 0,
  };
}

export const transformPrice = (dto: PriceInfoDTO): PriceInfo => {
  const priceUnit = dto.priceUnit || 1;

  const priceScale = transformPriceScaling(dto.priceScale || [], priceUnit) as {
    minQty: number;
    price: PriceData;
  }[];
  const priceScaleMSP = transformPriceScaling(dto.priceScaleMSP || [], priceUnit);
  const priceScaleRSP = transformPriceScaling(dto.priceScaleRSP || [], priceUnit);
  const rebateScale = transformRebate(dto.rebateScale || []);
  const surchargeScale = transformSurcharge(dto.surchargeScale || []);
  const isTariffSurchargeApplicable = dto.isTariffSurchargeApplicable ?? false;

  return {
    sapMaterialNumber: dto.sapMaterialNumber,
    code: dto.code,
    priceReference: dto.priceReference ?? "",
    packCode: dto.packCode || undefined,

    hasPrice: dto.hasPrice,
    priceScale,
    priceScaleMSP,
    priceScaleRSP,

    isContractAvailable: dto.isContractAvailable || false,
    isFreshPnaCall: dto.isFreshPnaCall || false,

    listPrice: transformPriceDTO(adjustPriceValue(dto.listPrice)),
    rebateScale,
    surchargeScale,
    isTariffSurchargeApplicable,
    isSpecialPrice: dto.isSpecialPrice || false,

    priceUnit,
    showBetterDealMinQty: dto.minQtyAvailableScale ?? null,

    salesCampaign: transformSalesCampaign(dto.salesCampaign, priceScale),
  };
};

function transformSalesCampaign(
  dto: SalesCampaignPriceDTO | undefined,
  netPrice: ScalingPrice,
): SalesCampaignPrice | undefined {
  if (!dto) {
    return undefined;
  }
  const data: SalesCampaignPrice = {};
  for (const { key, value } of dto) {
    data[key] = (value.items ?? []).map((entry) => ({
      ...entry,
      salesCampaignPrice: createScalingSalesPrice(netPrice, entry.salesCampaignPrice),
      actualSalesPrice: transformPriceDTO(entry.salesCampaignPrice),
    }));
  }
  return data;
}

function createScalingSalesPrice(netPrice: ScalingPrice, salesPrice?: PriceData) {
  if (netPrice.length === 0 && salesPrice?.value) {
    return [
      {
        minQty: 1,
        price: salesPrice,
      },
    ];
  }

  return netPrice.map((entry) => ({
    minQty: entry.minQty,
    price: lower(salesPrice, entry.price),
  }));
}

// TEMP: This function rounds the price value base the currency has no decimal digits.
// This should be done by SAP, but since they send wrong prices, we need to fix them.....
function adjustPriceValue(price: PriceDataDTO | null | undefined): PriceData | undefined {
  if (!price) {
    return undefined;
  }

  const fractionDigitAmount = NF.getFractionDigits(price.currencyIso);
  const adjustedValue = fractionDigitAmount === 0 ? Math.round(price.value) : price.value;

  return {
    ...price,
    value: adjustedValue,
  };
}

function transformPriceScaling(data: MoxyMapDTO<number, PriceDataDTO | null> | null, priceUnit: number) {
  const priceScaling: { minQty: number; price?: PriceData }[] = [];
  if (data) {
    for (const { key, value: priceDto } of data) {
      const minQty = Number(key);
      const priceData = transformPriceDTO(adjustPriceValue(priceDto));
      priceScaling.push({ minQty, price: priceData });
    }
  }
  priceScaling.sort((a, b) => a.minQty - b.minQty);

  return priceScaling;
}

function transformRebate(data: MoxyMapDTO<number, string | null> | null) {
  const rebateList: { minQty: number; rebate?: string }[] = [];
  if (data) {
    for (const { key, value } of data) {
      const minQty = Number(key);
      const rebate: string | undefined = value !== null ? value : undefined;

      rebateList.push({ minQty, rebate });
    }
  }
  rebateList.sort((a, b) => a.minQty - b.minQty);

  return rebateList;
}
function lower(salesPrice: PriceData | undefined, price: PriceData): PriceData {
  // Pick the lower price between the sales price and the net price, unless net price is 0, then always pick the sales price
  if (price.value === 0) {
    return salesPrice ?? zero();
  }

  if (salesPrice) {
    return salesPrice.value < price.value ? salesPrice : price;
  }

  return price;
}

function transformSurcharge(data: MoxyMapDTO<number, number | null> | null) {
  const rebateList: { minQty: number; surcharge: number }[] = [];
  if (data) {
    for (const { key, value } of data) {
      const minQty = Number(key);
      const surcharge: number = value !== null ? value : 0;

      rebateList.push({ minQty, surcharge });
    }
  }
  rebateList.sort((a, b) => a.minQty - b.minQty);

  return rebateList;
}
